import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChevronDown, ChevronRight, Copy, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

const INTRO_MARKDOWN = `
# Universe Creation

Each entity within the organization must maintain what the FastCube team calls their “universe”. This is the set of MD5s your organization is able to target or has internal data on.

**Once your universe is established, you can unlock a variety of features in our FastCube systems:**

1. **Team Dashboard:** See various breakdowns of your overall universe.
2. **Offer Reports:** Learn about the profile attributes that work for your team's offers and optimize your audiences.
3. **Segmentation:** Run segments on your universe for internal targeting.
4. **Fast ID Report:** Upload any list of MD5s and get dashboard-level data on it. (Activity, Domain distribution, etc. More dimensions coming soon based on feedback.)

**To unlock these features, the following steps must be taken:**

- Upload your entire universe to load the system.
- Send incremental updates as new addresses are added.
- Resync the entire universe on some interval (monthly is our recommendation).

**Complete the following steps to get started:**

1. Make a request via slack in the #fastcube-admin channel for access, and Include the part of the organization you are part of (Admin Level, Madrivo, Zerobounce, Sparta, AEON, etc).
2. Once access is granted, you should be able to generate an API key for your organization in our app by logging in and navigating to the API area in the left navigation.
3. Create a directory on a server or laptop.
4. Create a python venv and install the requests and python-dotenv packages via pip.

    \`\`\`bash
    python3 -m venv .venv
    source .venv/bin/activate
    pip install requests python-dotenv
    \`\`\`

5. Create a .env file in the md5 directory that contains the following:

    \`\`\`
    CUBE_API_KEY="REPLACE WITH TOKEN FROM ADMIN"
    \`\`\`
`.trim();

function childrenToText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(childrenToText).join('');
  if (React.isValidElement(node) && node.props?.children != null) {
    return childrenToText(node.props.children);
  }
  return '';
}

function CodeBlock({ children, ...props }) {
  const code = childrenToText(children).replace(/\n$/, '');

  const copyCode = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="relative my-4 overflow-hidden rounded-lg bg-gray-100">
      <button
        type="button"
        onClick={copyCode}
        disabled={!code}
        className="absolute top-2 right-2 z-10 rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-40"
        aria-label="Copy code"
        title="Copy code"
      >
        <Copy className="h-4 w-4" />
      </button>
      <pre className="overflow-x-auto p-4 pr-12 text-gray-800" {...props}>
        {children}
      </pre>
    </div>
  );
}

const markdownComponents = {
  h1: ({ children, ...props }) => (
    <h1 className="mb-6 text-center text-2xl font-bold text-gray-900" {...props}>
      {children}
    </h1>
  ),
  p: ({ children, ...props }) => (
    <p className="my-3 leading-relaxed text-gray-700" {...props}>
      {children}
    </p>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-bold text-gray-900" {...props}>
      {children}
    </strong>
  ),
  ul: ({ children, ...props }) => (
    <ul className="my-3 list-disc list-outside space-y-1 pl-6 text-gray-700" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="my-3 list-decimal list-outside space-y-1 pl-6 text-gray-700" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-relaxed" {...props}>
      {children}
    </li>
  ),
  code: ({ className, children, ...props }) => {
    if (!className) {
      return (
        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm" {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className={`font-mono text-sm ${className}`} {...props}>
        {children}
      </code>
    );
  },
  pre: CodeBlock,
};

function HighlightedPythonSample() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const url = `${process.env.PUBLIC_URL || ''}/code/upload_universe_parts.py`;
    let cancelled = false;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load upload_universe_parts.py');
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setCode(text);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load upload_universe_parts.py');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const copyCode = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  if (error) {
    return (
      <div className="my-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="my-4 overflow-hidden rounded-lg border border-gray-200">
      <div className="flex items-center justify-between bg-gray-50 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex items-center gap-1 rounded p-0.5 text-sm font-medium text-gray-800 hover:bg-gray-200"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <span>upload_universe_parts.py</span>
        </button>
        <button
          type="button"
          onClick={copyCode}
          disabled={loading || !code}
          className="rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-900 disabled:pointer-events-none disabled:opacity-40"
          aria-label="Copy code"
          title="Copy code"
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>
      {expanded && (
        loading ? (
          <pre className="min-h-[12rem] bg-white p-4 pr-12 font-mono text-sm text-gray-700">
            Loading…
          </pre>
        ) : (
          <SyntaxHighlighter
            language="python"
            style={oneLight}
            customStyle={{
              margin: 0,
              borderRadius: 0,
              backgroundColor: '#ffffff',
              padding: '1rem 3rem 1rem 1rem',
              fontSize: '0.875rem',
              lineHeight: 1.625,
            }}
            codeTagProps={{
              style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
            }}
          >
            {code}
          </SyntaxHighlighter>
        )
      )}
    </div>
  );
}

function IntroContent() {
  return (
    <article className="mx-auto max-w-3xl">
      <ReactMarkdown components={markdownComponents}>{INTRO_MARKDOWN}</ReactMarkdown>
      <ol start={6} className="my-3 list-decimal list-outside space-y-1 pl-6 text-gray-700">
        <li className="leading-relaxed">
          <p className="my-3">
            Copy the following python code into a file called upload_universe_parts.py:
          </p>
          <HighlightedPythonSample />
        </li>
        <li className="leading-relaxed">
          Run via your python venv:{' '}
          <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm">
            python upload_universe_parts.py &lt;FILENAME&gt;
          </code>
          <ul className="my-2 list-disc list-outside space-y-1 pl-6 text-gray-700">
            <li>
              The script waits until processing finishes and prints the upload report.
            </li>
          </ul>
        </li>
      </ol>
      <div className="mt-6 rounded-md border border-blue-200 bg-blue-50 p-4" role="note">
        <div className="flex">
          <Info className="h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden />
          <p className="ml-3 text-sm text-blue-800">
            Contact{' '}
            <a
              href="mailto:dev@fastcube.com"
              className="font-medium text-blue-800 underline hover:text-blue-900"
            >
              dev@fastcube.com
            </a>{' '}
            if you have any issues.
          </p>
        </div>
      </div>
    </article>
  );
}

export default IntroContent;
